import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import moment from 'moment';

type SearchForm = {
    start: FormControl<Date | null>;
    end: FormControl<Date | null>;
    searchValue: FormControl<string>;
    customerPurchaseNumber: FormControl<string>;
    submissionNumber: FormControl<string>;
    offerNumber: FormControl<string>;
};

type ControlName = 'searchValue' | 'customerPurchaseNumber' | 'submissionNumber' | 'offerNumber';

type FieldDef = Readonly<{
    control: ControlName;
    placeholder: string;
}>;

@Component({
    selector: 'app-search-and-filter',
    templateUrl: './search-and-filter.component.html',
    styleUrls: ['./search-and-filter.component.scss'],
})
export class SearchAndFilterComponent {
    @Input() selectedTab: string;
    @Output() offerSearch = new EventEmitter<{
        fromDate: Date | null;
        toDate: Date;
        search?: string;
        customerPurchaseNumber?: string;
        submissionNumber?: string;
        offerNumber?: string;
    }>();
    maxDate = moment().utc();
    form = new FormGroup<SearchForm>({
        start: new FormControl<Date | null>(null),
        end: new FormControl<Date | null>(null),
        searchValue: new FormControl<string>(''),
        customerPurchaseNumber: new FormControl<string>(''),
        submissionNumber: new FormControl<string>(''),
        offerNumber: new FormControl<string>(''),
    });
    offerFields: FieldDef[] = [
        { control: 'customerPurchaseNumber', placeholder: 'OVERVIEW.CUSTOMER_PURCHASE_NUMBER_PLACEHOLDER' },
        { control: 'submissionNumber', placeholder: 'OVERVIEW.SUBMISSION_NUMBER_PLACEHOLDER' },
        { control: 'offerNumber', placeholder: 'OVERVIEW.OFFER_NUMBER_PLACEHOLDER' },
    ];
    templateFields: FieldDef[] = [{ control: 'searchValue', placeholder: 'OVERVIEW.SEARCH_PLACEHOLDER' }];

    get isOffersTab() {
        return this.selectedTab === 'offers';
    }

    get activeSearchFields() {
        return this.isOffersTab ? this.offerFields : this.templateFields;
    }

    resetDate() {
        this.form.controls.start.setValue(null);
        this.form.controls.end.setValue(null);
        this.triggerSearch();
    }

    resetField(controlName: ControlName) {
        this.form.controls[controlName].setValue('');
        this.triggerSearch();
    }

    hasAtLeastOneValue(): boolean {
        const hasDateRange = !!this.form.controls.start.value && !!this.form.controls.end.value;
        const hasOffersText =
            !!this.form.controls.customerPurchaseNumber.value?.trim() ||
            !!this.form.controls.submissionNumber.value?.trim() ||
            !!this.form.controls.offerNumber.value?.trim();
        const hasGenericText = !!this.form.controls.searchValue.value?.trim();
        return this.isOffersTab ? hasOffersText || hasDateRange : hasGenericText || hasDateRange;
    }

    triggerSearch() {
        const { start, end, searchValue, submissionNumber, offerNumber } = this.form.controls;
        const toDate = end.value ?? new Date();
        const toDatePlus1 = moment(toDate).add(1, 'days').startOf('day').toDate();

        const customerPurchaseNumber = this.form.controls.customerPurchaseNumber.value.trim();

        const payload = {
            fromDate: start.value ?? null,
            toDate: toDatePlus1,
            search: searchValue.value.trim(),
            customerPurchaseNumber: customerPurchaseNumber
                ? `*${customerPurchaseNumber.replace(/\s/g, '*')}*`
                : undefined,
            submissionNumber: submissionNumber.value.trim(),
            offerNumber: offerNumber.value.trim(),
        };
        this.offerSearch.emit(payload);
    }
}
